import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from '../../../app/LanguageContext';
import { subtleSurfaceBg } from '../../../theme/surfaceSx';
import {
  createCustomizationField,
  updateCustomizationField,
  deleteCustomizationField,
} from '../../../app/api';
import { CustomizationFieldTypeSelect } from './VariantDetailDialog';

export default function CustomizationFieldsSection({
  productId,
  fields = [],
  onReload,
  onError,
}) {
  const { translate } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    name: '',
    is_required: false,
    customizable_type: 'TEXT',
    allow_other: false,
  });

  const openCreate = () => {
    setEditing(null);
    setDraft({ name: '', is_required: false, customizable_type: 'TEXT', allow_other: false });
    setDialogOpen(true);
  };

  const openEdit = (field) => {
    setEditing(field);
    setDraft({
      name: field.name,
      is_required: field.is_required,
      customizable_type: field.customizable_type || 'TEXT',
      allow_other: field.allow_other,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!productId || !draft.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        is_required: draft.is_required,
        customizable_type: (draft.customizable_type || 'TEXT').toLowerCase(),
        allow_other: draft.allow_other,
        display_order: editing?.display_order ?? fields.length,
      };
      if (editing?.id) {
        await updateCustomizationField(productId, editing.id, payload);
      } else {
        await createCustomizationField(productId, payload);
      }
      setDialogOpen(false);
      onReload?.();
    } catch (err) {
      onError?.(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm(translate('products.customization_delete_confirm'))) return;
    try {
      await deleteCustomizationField(productId, fieldId);
      onReload?.();
    } catch (err) {
      onError?.(err.message);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <Typography variant="h6" component="h2">
          {translate('products.customization_section')}
        </Typography>
        <IconButton size="small" aria-label={expanded ? 'Recolher' : 'Expandir'}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          {translate('products.customization_section_hint')}
        </Typography>
        {fields.map((field) => (
          <Paper
            key={field.id}
            variant="outlined"
            sx={{
              p: 1.5,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: subtleSurfaceBg,
            }}
          >
            <Typography variant="body2">
              <strong>{field.name}</strong>
              {field.customizable_type && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({translate(`products.customization_type_${(field.customizable_type || 'TEXT').toLowerCase()}`)})
                </Typography>
              )}
            </Typography>
            <Box>
              <IconButton size="small" onClick={() => openEdit(field)}>
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => handleDelete(field.id)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          </Paper>
        ))}
        <Button startIcon={<AddIcon />} onClick={openCreate} sx={{ mt: 1 }}>
          {translate('products.customization_add')}
        </Button>
      </Collapse>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {editing ? translate('products.customization_edit') : translate('products.customization_add')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label={translate('products.customization_name')}
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              fullWidth
            />
            <CustomizationFieldTypeSelect
              label={translate('products.customization_type')}
              value={draft.customizable_type}
              onChange={(v) => setDraft((d) => ({ ...d, customizable_type: v }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={draft.is_required}
                  onChange={(e) => setDraft((d) => ({ ...d, is_required: e.target.checked }))}
                />
              }
              label={translate('products.customization_required')}
            />
            {draft.customizable_type === 'SELECT' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draft.allow_other}
                    onChange={(e) => setDraft((d) => ({ ...d, allow_other: e.target.checked }))}
                  />
                }
                label={translate('products.customization_allow_other')}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{translate('common.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !draft.name.trim()}>
            {saving ? translate('common.saving') : translate('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
